import SortIcon from '@src/svgs/SortIcon';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import LongArrowUpIcon from '../../svgs/LongArrowUpIcon';
import { PhotoArtifactTab } from '../../types.ts/story';
import FileUpload from '../common/FileUpload';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import SearchInput from './SearchInput';

import type { SortBy } from '../../utility/general';

interface PhotoTabActionsProps {
  tab: PhotoArtifactTab;
  sortBy: SortBy;
  uploadButtonActive: boolean;
  onSearch: (searchTerm: string) => void;
  onSort: () => void;
  onUploadClick: () => void;
  onUploadCallback: () => Promise<void>;
  setIsLoading: (loading: boolean) => void;
}

const PhotoTabActions: React.FC<PhotoTabActionsProps> = ({
  tab,
  sortBy,
  uploadButtonActive,
  onSearch,
  onSort,
  onUploadClick,
  onUploadCallback,
  setIsLoading,
}) => {
  const renderStockTabActions = () => (
    <PhotoTopAction>
      <SearchInput
        placeholder="Search photo"
        handleAction={onSearch}
        radius="var(--radius-input)"
        iconRight
      />
    </PhotoTopAction>
  );

  const renderAiTabActions = () => (
    <PhotoTopAction>
      <SearchInput
        placeholder="Describe the photo you want to see and hit enter"
        handleAction={onSearch}
        expandHeight
        hideIcons
        radius="var(--radius-input)"
      />
    </PhotoTopAction>
  );

  const renderArtifactTabActions = () => (
    <PhotoTopAction>
      <SearchSortWrapper>
        <SearchInput
          placeholder="Search"
          handleAction={onSearch}
          radius="var(--radius-input)"
          iconRight
        />
        <SortIconWrapper
          onClick={onSort}
          data-tooltip-id="sort-tooltip"
          data-tooltip-content={`Sort by Upload Date: ${
            sortBy === '-createdAt' ? 'Oldest' : 'Newest'
          }`}
        >
          <SortIcon width="16" height="16" fillColor="currentColor" />
          <Tooltip
            id="sort-tooltip"
            style={COMPACT_TOOLTIP_STYLE}
            place="top"
            positionStrategy="fixed"
          />
        </SortIconWrapper>
      </SearchSortWrapper>
      <FileUpload
        width="100%"
        type={(() => {
          if (tab === PhotoArtifactTab.story) return 'storyArtifacts';
          if (tab === PhotoArtifactTab.logo) return 'organizationLogos';
          return 'organizationArtifacts';
        })()}
        Button={
          <AddFileButton isActivated={uploadButtonActive}>
            <LongArrowUpIcon width="12" height="14" fillColor="currentColor" />{' '}
            Upload Photo
          </AddFileButton>
        }
        onClick={onUploadClick}
        callback={onUploadCallback}
        setIsLoading={setIsLoading}
      />
    </PhotoTopAction>
  );

  switch (tab) {
    case PhotoArtifactTab.stock:
      return renderStockTabActions();
    case PhotoArtifactTab.ai:
      return renderAiTabActions();
    case PhotoArtifactTab.story:
    case PhotoArtifactTab.album:
    case PhotoArtifactTab.logo:
      return renderArtifactTabActions();
    default:
      return null;
  }
};

export default PhotoTabActions;

const PhotoTopAction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SearchSortWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  > form {
    flex: 1;
  }
`;

const SortIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid var(--border);
  color: var(--text-muted);
  transition: all 150ms ease-out;
  flex-shrink: 0;

  &:hover {
    border-color: var(--foreground);
    color: var(--foreground);
  }
`;

const AddFileButton = styled.button<{ isActivated?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: var(--btn-h-lg);
  padding: 0 var(--btn-px-lg);
  background: var(--primary);
  color: var(--primary-foreground);
  border: none;
  border-radius: var(--radius-button);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
  transition: background 120ms ease;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:active:not(:disabled) {
    background: var(--primary-pressed);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
